import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router-dom";
import { api, ApiError } from "../api";
import { Modal } from "../components/Modal";
import { NetTag, netColor } from "../components/NetDirection";
import { fmtINR } from "../fmt";

interface Party {
  id: string;
  name: string;
  kind: "person" | "firm" | "other";
  relation: string | null;
  notes: string | null;
}

interface LedgerOverviewRow {
  id: string;
  name: string;
  kind: string;
  txnCount: number;
  totalDr: number;
  totalCr: number;
  netBank: number;
  netAll: number;
}

const emptyForm = { name: "", kind: "person" as Party["kind"], relation: "", notes: "" };

export function Khaata() {
  const [rows, setRows] = useState<LedgerOverviewRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Party | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    api
      .get<LedgerOverviewRow[]>("/api/parties/khaata-overview")
      .then((r) => setRows(r))
      .catch((err) => setError(err instanceof ApiError ? err.message : "failed to load ledgers"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setFormError(null);
    setModalOpen(true);
  };

  const openEdit = async (id: string) => {
    try {
      const p = await api.get<Party>(`/api/parties/${id}`);
      setEditing(p);
      setForm({ name: p.name, kind: p.kind, relation: p.relation ?? "", notes: p.notes ?? "" });
      setFormError(null);
      setModalOpen(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "could not load party");
    }
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const body = {
      name: form.name,
      kind: form.kind,
      relation: form.relation || null,
      notes: form.notes || null,
    };
    try {
      if (editing) await api.patch(`/api/parties/${editing.id}`, body);
      else await api.post("/api/parties", body);
      setModalOpen(false);
      load();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : "save failed");
    }
  };

  const onDelete = async (row: LedgerOverviewRow) => {
    if (!confirm(`Delete party "${row.name}"?`)) return;
    try {
      await api.delete(`/api/parties/${row.id}`);
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "delete failed");
    }
  };

  // Shared convention (NetDirection): positive = receivable by you, negative =
  // payable by you. Colour + tag follow the sign, identically everywhere.
  const netCell = (id: string, value: number) => (
    <Link
      to={`/khaata/${id}`}
      className="num"
      style={{ color: netColor(value), display: "inline-flex", gap: "0.4rem", alignItems: "baseline", justifyContent: "flex-end" }}
    >
      {fmtINR(value)} <NetTag value={value} />
    </Link>
  );

  return (
    <div>
      <h1>Party Ledgers</h1>
      <div className="muted" style={{ marginBottom: "0.75rem" }}>
        Net is shown as <strong>receivable by you</strong>: positive (green) =
        the party owes you; negative (red) = you owe the party.
      </div>
      {error && <div className="error-text">{error}</div>}
      <div className="toolbar">
        <button className="primary" type="button" onClick={openAdd}>
          Add party
        </button>
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Kind</th>
              <th className="num">Txns</th>
              <th className="num">Total Dr (paid by you)</th>
              <th className="num">Total Cr (received)</th>
              <th className="num">Net receivable by you — Bank (₹)</th>
              <th className="num">Net receivable by you — Bank + Card (₹)</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>
                  <Link to={`/khaata/${r.id}`}>{r.name}</Link>
                </td>
                <td>{r.kind}</td>
                <td className="num">
                  <Link to={`/khaata/${r.id}`}>{r.txnCount}</Link>
                </td>
                <td className="num">
                  <Link to={`/khaata/${r.id}`} style={{ color: "var(--accent)" }}>
                    {fmtINR(r.totalDr)}
                  </Link>
                </td>
                <td className="num">
                  <Link to={`/khaata/${r.id}`} style={{ color: "var(--accent)" }}>
                    {fmtINR(r.totalCr)}
                  </Link>
                </td>
                <td className="num">{netCell(r.id, r.netBank)}</td>
                <td className="num">{netCell(r.id, r.netAll)}</td>
                <td>
                  <span className="actions-inline">
                    <button type="button" onClick={() => openEdit(r.id)}>
                      Edit
                    </button>
                    <button type="button" className="danger" onClick={() => onDelete(r)}>
                      Delete
                    </button>
                  </span>
                </td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={8} className="empty-state">
                  No parties yet.
                </td>
              </tr>
            )}
            {loading && (
              <tr>
                <td colSpan={8} className="empty-state">
                  Loading…
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <Modal title={editing ? "Edit party" : "Add party"} onClose={() => setModalOpen(false)}>
          <form onSubmit={onSubmit} className="modal-body">
            <div className="field">
              <label htmlFor="p-name">Name</label>
              <input
                id="p-name"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor="p-kind">Kind</label>
              <select
                id="p-kind"
                value={form.kind}
                onChange={(e) => setForm({ ...form, kind: e.target.value as Party["kind"] })}
              >
                <option value="person">Person</option>
                <option value="firm">Firm</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="field">
              <label htmlFor="p-relation">Relation</label>
              <input
                id="p-relation"
                value={form.relation}
                onChange={(e) => setForm({ ...form, relation: e.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor="p-notes">Notes</label>
              <textarea
                id="p-notes"
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>
            {formError && <div className="error-text">{formError}</div>}
            <div className="modal-actions">
              <button type="button" onClick={() => setModalOpen(false)}>
                Cancel
              </button>
              <button className="primary" type="submit">
                Save
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
